import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Search, Plus, MessageCircle, User, Menu, X, LogOut, LayoutDashboard, ChevronDown, MapPin, Map } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Don't show on auth pages
  if (location.pathname === '/auth' || location.pathname === '/auth/callback') return null;

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-zinc-200" data-testid="main-header">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
            <span className="text-xl font-black tracking-tighter text-zinc-900">Durham<span className="text-[#002FA7]">Market</span></span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/browse" className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="nav-browse">Browse</Link>
            <Link to="/map" className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors flex items-center gap-1" data-testid="nav-map">
              <Map className="w-4 h-4" /> Map
            </Link>
            {user && (
              <>
                <Link to="/messages" className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors relative" data-testid="nav-messages">
                  <MessageCircle className="w-5 h-5" />
                </Link>
              </>
            )}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Link
                  to="/post"
                  className="hidden sm:inline-flex bg-[#002FA7] text-white hover:bg-[#002585] transition-colors rounded-full px-5 py-2 font-bold text-sm items-center gap-1.5"
                  data-testid="post-ad-btn"
                >
                  <Plus className="w-4 h-4" /> Post Ad
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-zinc-200 hover:bg-zinc-50 transition-colors" data-testid="user-menu-btn">
                      <div className="w-7 h-7 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 overflow-hidden">
                        {user.avatar ? <img src={user.avatar} alt="" className="w-7 h-7 rounded-full object-cover" /> : user.name?.charAt(0)}
                      </div>
                      <span className="hidden sm:inline text-sm font-medium text-zinc-700">{user.name?.split(' ')[0]}</span>
                      <ChevronDown className="w-3 h-3 text-zinc-400" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2 border-b border-zinc-100">
                      <p className="text-sm font-semibold text-zinc-900">{user.name}</p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                    </div>
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer" data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer" data-testid="menu-admin">
                        <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Panel
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate(`/profile/${user.user_id}`)} className="cursor-pointer" data-testid="menu-profile">
                      <User className="w-4 h-4 mr-2" /> My Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/messages')} className="cursor-pointer" data-testid="menu-messages">
                      <MessageCircle className="w-4 h-4 mr-2" /> Messages
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" /> Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/auth?mode=login" className="text-sm font-medium text-zinc-600 hover:text-zinc-900 px-4 py-2" data-testid="login-btn">Sign In</Link>
                <Link to="/auth?mode=register" className="bg-[#002FA7] text-white hover:bg-[#002585] rounded-full px-5 py-2 font-bold text-sm" data-testid="register-btn">Get Started</Link>
              </div>
            )}

            {/* Mobile Menu */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2" data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-zinc-200 py-4 space-y-2 animate-fade-in-up" data-testid="mobile-menu">
            <Link to="/browse" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 rounded-lg">Browse Listings</Link>
            <Link to="/map" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 rounded-lg">Map View</Link>
            {user && (
              <>
                <Link to="/post" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 rounded-lg">Post Ad</Link>
                <Link to="/messages" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 rounded-lg">Messages</Link>
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 rounded-lg">Dashboard</Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
